import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  useColorMode,
  Icon,
  Flex,
  Image,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import {
  FaShieldAlt,
  FaBolt,
  FaUsers,
  FaPalette,
  FaLock,
  FaGlobe,
  FaRocket,
  FaGithub,
  FaCheckCircle,
  FaDice,
  FaGamepad,
} from 'react-icons/fa';
import { GiPokerHand, GiSpades, GiCardPlay, GiChainLightning, GiAtom } from 'react-icons/gi';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../brandConfig';
import ListBuildingModal from '../components/ListBuildingModal';

// Poker VM inspired colors
const pvmColors = {
  blue1: '#3d59a1',
  blue2: '#2a488f',
  blue3: '#4263af',
  blue4: '#1e346b',
  blue5: '#324f97',
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Sunshine ray flicker animations
const flicker1 = keyframes`
  0% { opacity: 0; }
  10% { opacity: 0.5; }
  30% { opacity: 0.3; }
  50% { opacity: 0; }
  70% { opacity: 0.4; }
  100% { opacity: 0; }
`;

const flicker2 = keyframes`
  0% { opacity: 0.3; }
  25% { opacity: 0; }
  40% { opacity: 0.5; }
  60% { opacity: 0.2; }
  85% { opacity: 0; }
  100% { opacity: 0.3; }
`;

const flicker3 = keyframes`
  0% { opacity: 0; }
  20% { opacity: 0.6; }
  45% { opacity: 0; }
  65% { opacity: 0.3; }
  90% { opacity: 0.5; }
  100% { opacity: 0; }
`;

const sunshineMain = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.5; }
`;

// Typing animation hook - starts with first word visible
const useTypingEffect = (words: string[], typingSpeed = 60, deletingSpeed = 30, pauseDuration = 1500) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState(words[0] || ''); // Start with first word
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Skip the initial typing on first render since we start with text
  useEffect(() => {
    if (isFirstRender && currentText === words[0]) {
      const timer = setTimeout(() => {
        setIsFirstRender(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(timer);
    }
  }, [isFirstRender, currentText, words, pauseDuration]);

  useEffect(() => {
    if (isFirstRender) return; // Skip main effect during first render

    const word = words[currentWordIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < word.length) {
          setCurrentText(word.slice(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(word.slice(0, currentText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex, words, typingSpeed, deletingSpeed, pauseDuration, isFirstRender]);

  return currentText;
};

// Animated counter hook
const useCountUp = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(!startOnView);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (startOnView && ref.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

// Sunshine Background Component - Brighter white rays
const SunshineBackground = () => {
  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      pointerEvents="none"
      zIndex={0}
      overflow="hidden"
    >
      {/* Main wide sunshine beam - brighter for visibility */}
      <Box
        position="absolute"
        top="-30%"
        left="5%"
        width="0"
        height="0"
        borderStyle="solid"
        borderWidth="0 22vw 150vh 22vw"
        borderColor="transparent transparent rgba(255, 255, 255, 0.18) transparent"
        transform="rotate(-20deg)"
        transformOrigin="top center"
        animation={`${sunshineMain} 12s ease-in-out infinite`}
        filter="blur(40px)"
      />

      {/* Secondary beam */}
      <Box
        position="absolute"
        top="-20%"
        left="20%"
        width="0"
        height="0"
        borderStyle="solid"
        borderWidth="0 15vw 130vh 15vw"
        borderColor="transparent transparent rgba(255, 255, 255, 0.12) transparent"
        transform="rotate(-10deg)"
        transformOrigin="top center"
        animation={`${sunshineMain} 15s ease-in-out infinite 2s`}
        filter="blur(35px)"
      />

      {/* Third beam from right side */}
      <Box
        position="absolute"
        top="-25%"
        right="10%"
        width="0"
        height="0"
        borderStyle="solid"
        borderWidth="0 18vw 140vh 18vw"
        borderColor="transparent transparent rgba(255, 255, 255, 0.10) transparent"
        transform="rotate(15deg)"
        transformOrigin="top center"
        animation={`${sunshineMain} 18s ease-in-out infinite 4s`}
        filter="blur(45px)"
      />

      {/* Flickering God Rays - Brighter */}
      <Box
        position="absolute"
        top="-10%"
        left="8%"
        width="4px"
        height="120%"
        background="rgba(255, 255, 255, 0.20)"
        transform="rotate(-18deg)"
        transformOrigin="top left"
        animation={`${flicker1} 6s ease-in-out infinite`}
        filter="blur(2px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="18%"
        width="3px"
        height="120%"
        background="rgba(255, 255, 255, 0.18)"
        transform="rotate(-15deg)"
        transformOrigin="top left"
        animation={`${flicker2} 8s ease-in-out infinite 1s`}
        filter="blur(1.5px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="26%"
        width="3.5px"
        height="120%"
        background="rgba(255, 255, 255, 0.22)"
        transform="rotate(-12deg)"
        transformOrigin="top left"
        animation={`${flicker3} 7s ease-in-out infinite 2s`}
        filter="blur(2px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="35%"
        width="2.5px"
        height="120%"
        background="rgba(255, 255, 255, 0.15)"
        transform="rotate(-10deg)"
        transformOrigin="top left"
        animation={`${flicker1} 9s ease-in-out infinite 3s`}
        filter="blur(1.5px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="42%"
        width="3px"
        height="120%"
        background="rgba(255, 255, 255, 0.18)"
        transform="rotate(-8deg)"
        transformOrigin="top left"
        animation={`${flicker2} 5.5s ease-in-out infinite 1.5s`}
        filter="blur(1.5px)"
      />
      {/* Additional rays for more coverage */}
      <Box
        position="absolute"
        top="-10%"
        left="50%"
        width="3px"
        height="120%"
        background="rgba(255, 255, 255, 0.16)"
        transform="rotate(-5deg)"
        transformOrigin="top left"
        animation={`${flicker3} 7.5s ease-in-out infinite 0.5s`}
        filter="blur(1.5px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="58%"
        width="2.5px"
        height="120%"
        background="rgba(255, 255, 255, 0.14)"
        transform="rotate(-3deg)"
        transformOrigin="top left"
        animation={`${flicker1} 8.5s ease-in-out infinite 2.5s`}
        filter="blur(1px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="66%"
        width="3px"
        height="120%"
        background="rgba(255, 255, 255, 0.17)"
        transform="rotate(2deg)"
        transformOrigin="top left"
        animation={`${flicker2} 6.5s ease-in-out infinite 1s`}
        filter="blur(1.5px)"
      />
      <Box
        position="absolute"
        top="-10%"
        left="75%"
        width="2.5px"
        height="120%"
        background="rgba(255, 255, 255, 0.15)"
        transform="rotate(5deg)"
        transformOrigin="top left"
        animation={`${flicker3} 9s ease-in-out infinite 2s`}
        filter="blur(1px)"
      />
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ value, label, suffix = '' }: { value: number; label: string; suffix?: string }) => {
  const { count, ref } = useCountUp(value);
  const { colorMode } = useColorMode();

  return (
    <VStack
      ref={ref}
      spacing={1}
      p={6}
      bg="rgba(255, 255, 255, 0.03)"
      backdropFilter="blur(10px)"
      borderRadius="xl"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-5px)',
        bg: 'rgba(255, 255, 255, 0.05)',
        borderColor: 'rgba(59, 130, 246, 0.3)',
      }}
    >
      <Text
        fontSize={{ base: '3xl', md: '4xl' }}
        fontWeight="900"
        bgGradient="linear(to-r, #3b82f6, #64ffda)"
        bgClip="text"
        letterSpacing="-0.02em"
      >
        {count.toLocaleString()}{suffix}
      </Text>
      <Text
        fontSize="sm"
        color={getColor('text.secondary', colorMode)}
        textTransform="uppercase"
        letterSpacing="0.1em"
        fontWeight="600"
      >
        {label}
      </Text>
    </VStack>
  );
};

// Feature Card Component
const FeatureCard = ({
  icon,
  title,
  description
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) => {
  const { colorMode } = useColorMode();

  return (
    <VStack
      p={6}
      bg="rgba(255, 255, 255, 0.03)"
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.08)"
      spacing={4}
      align="start"
      transition="all 0.3s ease"
      _hover={{
        transform: 'translateY(-8px)',
        bg: 'rgba(255, 255, 255, 0.06)',
        borderColor: 'rgba(59, 130, 246, 0.4)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
      }}
      h="full"
    >
      <Flex
        w="56px"
        h="56px"
        borderRadius="xl"
        bgGradient="linear(135deg, #3b82f6, #2563eb)"
        justify="center"
        align="center"
        boxShadow="0 8px 20px rgba(59, 130, 246, 0.3)"
      >
        <Icon as={icon} boxSize={6} color="white" />
      </Flex>
      <Heading
        size="md"
        color={getColor('text.primary', colorMode)}
        fontWeight="700"
        letterSpacing="-0.01em"
      >
        {title}
      </Heading>
      <Text
        color={getColor('text.secondary', colorMode)}
        fontSize="sm"
        lineHeight="1.7"
      >
        {description}
      </Text>
    </VStack>
  );
};

// Partner Badge Component
const PartnerBadge = ({
  icon,
  label,
  subLabel
}: {
  icon?: React.ElementType;
  label: string;
  subLabel?: string;
}) => {
  return (
    <HStack
      px={5}
      py={3}
      bg="rgba(255, 255, 255, 0.03)"
      backdropFilter="blur(10px)"
      borderRadius="xl"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      spacing={3}
      transition="all 0.3s ease"
      _hover={{
        bg: 'rgba(255, 255, 255, 0.06)',
        borderColor: 'rgba(59, 130, 246, 0.3)',
        transform: 'translateY(-2px)',
      }}
    >
      {icon && <Icon as={icon} boxSize={5} color="gray.400" />}
      <VStack spacing={0} align="start">
        <Text fontSize="sm" fontWeight="600" color="white">
          {label}
        </Text>
        {subLabel && (
          <Text fontSize="xs" color="gray.500">
            {subLabel}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Typing effect for headline
  const typingText = useTypingEffect(
    ['Decentralized', 'Transparent', 'Yours', 'Fair', 'Borderless'],
    100,
    50,
    2000
  );

  const features = [
    {
      icon: FaShieldAlt,
      title: 'Provably Fair',
      description: 'Every hand verified on-chain. Smart contracts ensure complete transparency and fairness.',
    },
    {
      icon: FaBolt,
      title: 'Instant Payouts',
      description: 'No banking delays. Withdraw your winnings instantly to your crypto wallet.',
    },
    {
      icon: FaLock,
      title: 'Advanced Security',
      description: 'Multi-layered anti-collusion systems and cryptographic protection for every game.',
    },
    {
      icon: FaPalette,
      title: 'Custom Branding',
      description: 'Launch your own branded card game club. Full white-label customization in minutes.',
    },
    {
      icon: FaUsers,
      title: 'Player Management',
      description: 'Complete tools for managing players, tables, tournaments, and club operations.',
    },
    {
      icon: FaGlobe,
      title: 'Global Access',
      description: 'Borderless gaming. Players worldwide can join without geographic restrictions.',
    },
  ];

  return (
    <Box
      minH="100vh"
      bg="#0a0a0f"
      position="relative"
      overflow="hidden"
    >
      {/* Sunshine Rays Background */}
      <SunshineBackground />

      {/* Animated Background Gradient Orbs */}
      <Box
        position="absolute"
        top="-20%"
        left="-10%"
        w="60%"
        h="60%"
        bgGradient="radial(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)"
        filter="blur(60px)"
        animation={`${pulse} 8s ease-in-out infinite`}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-20%"
        right="-10%"
        w="50%"
        h="50%"
        bgGradient="radial(circle, rgba(100, 255, 218, 0.1) 0%, transparent 70%)"
        filter="blur(60px)"
        animation={`${pulse} 10s ease-in-out infinite 2s`}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="40%"
        right="20%"
        w="30%"
        h="30%"
        bgGradient="radial(circle, rgba(168, 85, 247, 0.08) 0%, transparent 70%)"
        filter="blur(40px)"
        animation={`${float} 12s ease-in-out infinite`}
        pointerEvents="none"
      />

      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Container maxW="6xl" pt={{ base: 16, md: 24 }} pb={16} position="relative">
        <VStack spacing={8} animation={`${fadeInUp} 0.8s ease-out`}>
          {/* Badge */}
          <HStack
            px={4}
            py={2}
            bg="rgba(59, 130, 246, 0.1)"
            borderRadius="full"
            border="1px solid"
            borderColor="rgba(59, 130, 246, 0.3)"
          >
            <Box w={2} h={2} borderRadius="full" bg="#64ffda" />
            <Text fontSize="sm" color="#64ffda" fontWeight="600">
              Now Live on Ethereum
            </Text>
          </HStack>

          {/* Main Heading with Typing Effect */}
          <VStack spacing={4} textAlign="center">
            <Heading
              as="h1"
              fontSize={{ base: '4xl', md: '6xl', lg: '7xl' }}
              fontWeight="900"
              letterSpacing="-0.03em"
              lineHeight="1.1"
              color="white"
            >
              The Future of Card Games
              <br />
              <Text as="span" color="white">is </Text>
              <Text
                as="span"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
                minW={{ base: '200px', md: '350px' }}
                display="inline-block"
              >
                {typingText}
                <Text
                  as="span"
                  animation={`${pulse} 1s ease-in-out infinite`}
                  color="#64ffda"
                >
                  |
                </Text>
              </Text>
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color="gray.400"
              maxW="2xl"
              lineHeight="1.8"
            >
              Build and launch your own card game club on the blockchain.
              Provably fair games, instant payouts, and complete transparency.
              No middlemen. No delays. Just cards.
            </Text>
          </VStack>

          {/* CTA Buttons */}
          <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
            <Button
              size="lg"
              px={8}
              py={7}
              bgGradient="linear(135deg, #3b82f6, #2563eb)"
              color="white"
              fontSize="lg"
              fontWeight="bold"
              borderRadius="xl"
              boxShadow="0 8px 30px rgba(59, 130, 246, 0.4)"
              _hover={{
                bgGradient: 'linear(135deg, #4f91ff, #3b82f6)',
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.5)',
              }}
              transition="all 0.3s ease"
              onClick={() => setIsModalOpen(true)}
            >
              Start Your Card Club
            </Button>
            <Button
              size="lg"
              px={8}
              py={7}
              variant="outline"
              color="white"
              borderColor="rgba(255, 255, 255, 0.2)"
              borderRadius="xl"
              fontSize="lg"
              fontWeight="600"
              _hover={{
                bg: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                transform: 'translateY(-3px)',
              }}
              transition="all 0.3s ease"
              onClick={() => navigate('/timeline')}
            >
              View Roadmap
            </Button>
          </HStack>

          {/* Microcopy */}
          <Text fontSize="sm" color="gray.500">
            No credit card required • Takes less than 60 seconds
          </Text>
        </VStack>
      </Container>

      {/* Partner Logos / Trust Badges */}
      <Box py={8} borderTop="1px solid" borderColor="rgba(255, 255, 255, 0.05)">
        <Container maxW="5xl">
          <VStack spacing={6}>
            <Text
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.2em"
            >
              Built With
            </Text>
            <Flex
              gap={4}
              flexWrap="wrap"
              justify="center"
              align="center"
            >
              <PartnerBadge
                label="Ethereum"
                subLabel="Mainnet"
              />
              <PartnerBadge
                icon={FaGithub}
                label="Open Source"
                subLabel="MIT License"
              />
              <PartnerBadge
                icon={FaCheckCircle}
                label="Smart Contract Audited"
              />
              <PartnerBadge
                icon={GiChainLightning}
                label="Chainlink VRF"
                subLabel="Verifiable Randomness"
              />
              <PartnerBadge
                icon={GiAtom}
                label="Cosmos SDK"
                subLabel="Blockchain Framework"
              />
            </Flex>
          </VStack>
        </Container>
      </Box>

      {/* Stats Bar */}
      <Box
        py={12}
        bg="rgba(255, 255, 255, 0.02)"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="rgba(255, 255, 255, 0.05)"
      >
        <Container maxW="5xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
            <StatCard value={52} label="Validator Slots" />
            <StatCard value={1000} label="Early Adopters" suffix="+" />
            <StatCard value={99} label="Uptime" suffix="%" />
            <StatCard value={24} label="Hour Support" suffix="/7" />
          </SimpleGrid>
        </Container>
      </Box>

      {/* Video Section */}
      <Container maxW="5xl" py={20}>
        <VStack spacing={8} animation={`${fadeInUp} 0.8s ease-out`}>
          <VStack spacing={4} textAlign="center">
            <Heading
              size="xl"
              color="white"
              fontWeight="800"
              letterSpacing="-0.02em"
            >
              See Block52 in Action
            </Heading>
            <Text color="gray.400" maxW="xl">
              Watch how our decentralized card game platform delivers transparent,
              fair games powered by blockchain technology.
            </Text>
          </VStack>

          <Box
            w="full"
            bg="rgba(255, 255, 255, 0.03)"
            backdropFilter="blur(10px)"
            borderRadius="2xl"
            border="1px solid"
            borderColor="rgba(59, 130, 246, 0.3)"
            overflow="hidden"
            boxShadow="0 30px 60px rgba(0, 0, 0, 0.4)"
            transition="all 0.3s ease"
            _hover={{
              borderColor: 'rgba(59, 130, 246, 0.5)',
              boxShadow: '0 40px 80px rgba(0, 0, 0, 0.5)',
            }}
          >
            <video
              controls
              autoPlay
              muted
              loop
              playsInline
              style={{ width: '100%', height: 'auto', display: 'block' }}
            >
              <source
                src="https://files.tommillerservices.com/ipfs/QmUK8s41y5wUwmGnU2Tz1qC2wP2wHdX7WAic2fj97Zv5by"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Box>
        </VStack>
      </Container>

      {/* Features Grid */}
      <Container maxW="6xl" py={20}>
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Text
              fontSize="sm"
              color="#64ffda"
              textTransform="uppercase"
              letterSpacing="0.15em"
              fontWeight="700"
            >
              Why Block52
            </Text>
            <Heading
              size="xl"
              color="white"
              fontWeight="800"
              letterSpacing="-0.02em"
            >
              Everything You Need to Run a Card Club
            </Heading>
            <Text color="gray.400" maxW="2xl">
              Built from the ground up for transparency, security, and player trust.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
            {features.map((feature, index) => (
              <Box
                key={index}
                animation={`${fadeInUp} 0.6s ease-out`}
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
              >
                <FeatureCard {...feature} />
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Card Games Section */}
      <Box py={20} bg="rgba(255, 255, 255, 0.02)">
        <Container maxW="6xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Text
                fontSize="sm"
                color="#64ffda"
                textTransform="uppercase"
                letterSpacing="0.15em"
                fontWeight="700"
              >
                Layer 1 Card Game Blockchain
              </Text>
              <Heading
                size="xl"
                color="white"
                fontWeight="800"
                letterSpacing="-0.02em"
              >
                One Platform, Every Card Game
              </Heading>
              <Text color="gray.400" maxW="2xl">
                Block52 is a Layer 1 blockchain for any card game. Our smart contracts
                and game engine support multiple game types with the same provably fair guarantees.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} w="full">
              {/* Texas Hold'em - Available */}
              <VStack
                p={6}
                bg="rgba(59, 130, 246, 0.1)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="2px solid"
                borderColor="rgba(59, 130, 246, 0.4)"
                spacing={3}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-5px)',
                  borderColor: 'rgba(59, 130, 246, 0.6)',
                }}
              >
                <Icon as={GiPokerHand} boxSize={10} color="#3b82f6" />
                <Text fontWeight="700" color="white" fontSize="lg">Texas Hold'em</Text>
                <HStack>
                  <Box w={2} h={2} borderRadius="full" bg="green.400" />
                  <Text fontSize="xs" color="green.400" fontWeight="600">LIVE NOW</Text>
                </HStack>
              </VStack>

              {/* Omaha - Coming Soon */}
              <VStack
                p={6}
                bg="rgba(255, 255, 255, 0.03)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                spacing={3}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-5px)',
                  bg: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <Icon as={GiSpades} boxSize={10} color="gray.500" />
                <Text fontWeight="700" color="white" fontSize="lg">Omaha</Text>
                <HStack>
                  <Box w={2} h={2} borderRadius="full" bg="yellow.400" />
                  <Text fontSize="xs" color="yellow.400" fontWeight="600">COMING SOON</Text>
                </HStack>
              </VStack>

              {/* Blackjack - Coming Soon */}
              <VStack
                p={6}
                bg="rgba(255, 255, 255, 0.03)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                spacing={3}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-5px)',
                  bg: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <Icon as={GiCardPlay} boxSize={10} color="gray.500" />
                <Text fontWeight="700" color="white" fontSize="lg">Blackjack</Text>
                <HStack>
                  <Box w={2} h={2} borderRadius="full" bg="yellow.400" />
                  <Text fontSize="xs" color="yellow.400" fontWeight="600">COMING SOON</Text>
                </HStack>
              </VStack>

              {/* More Games - Future */}
              <VStack
                p={6}
                bg="rgba(255, 255, 255, 0.03)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="1px dashed"
                borderColor="rgba(255, 255, 255, 0.2)"
                spacing={3}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-5px)',
                  borderColor: 'rgba(100, 255, 218, 0.4)',
                }}
              >
                <Icon as={FaGamepad} boxSize={10} color="gray.600" />
                <Text fontWeight="700" color="gray.400" fontSize="lg">More Games</Text>
                <HStack>
                  <Text fontSize="xs" color="gray.500" fontWeight="600">PROPOSE A GAME</Text>
                </HStack>
              </VStack>
            </SimpleGrid>

            <Text color="gray.500" fontSize="sm" textAlign="center" maxW="xl">
              Our modular architecture means any card game can be built on Block52.
              Developers can create new games using our SDK and smart contract templates.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* License Partners Section */}
      <Box py={20}>
        <Container maxW="6xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Text
                fontSize="sm"
                color="#64ffda"
                textTransform="uppercase"
                letterSpacing="0.15em"
                fontWeight="700"
              >
                Trusted By
              </Text>
              <Heading
                size="xl"
                color="white"
                fontWeight="800"
                letterSpacing="-0.02em"
              >
                License Partners
              </Heading>
              <Text color="gray.400" maxW="2xl">
                Gaming operators already building on Block52. Join the growing network of
                licensed partners running provably fair card games on our blockchain.
              </Text>
            </VStack>

            <Flex
              gap={8}
              flexWrap="wrap"
              justify="center"
              align="center"
              w="full"
            >
              {/* Texas Hodl Partner Card */}
              <Box
                p={8}
                bg="rgba(255, 255, 255, 0.03)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-8px)',
                  bg: 'rgba(255, 255, 255, 0.06)',
                  borderColor: 'rgba(59, 130, 246, 0.4)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                }}
                minW={{ base: '280px', md: '320px' }}
              >
                <VStack spacing={4}>
                  <Box
                    as="a"
                    href="https://texashodl.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    cursor="pointer"
                  >
                    <Image
                      src="https://texashodl.net/wp-content/uploads/2024/01/logo_web2.png"
                      alt="Texas Hodl"
                      maxH="80px"
                      objectFit="contain"
                      transition="transform 0.2s"
                      _hover={{ transform: 'scale(1.05)' }}
                    />
                  </Box>
                  <VStack spacing={1}>
                    <Text fontWeight="700" color="white" fontSize="lg">
                      Texas Hodl
                    </Text>
                    <HStack>
                      <Box w={2} h={2} borderRadius="full" bg="green.400" />
                      <Text fontSize="xs" color="green.400" fontWeight="600">
                        LIVE PARTNER
                      </Text>
                    </HStack>
                  </VStack>
                  <Text fontSize="sm" color="gray.400" textAlign="center">
                    Decentralized Texas Hold'em platform
                  </Text>
                  <HStack spacing={3} pt={2}>
                    <Button
                      as="a"
                      href="https://texashodl.net/"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      variant="outline"
                      color="gray.300"
                      borderColor="rgba(255, 255, 255, 0.2)"
                      borderRadius="lg"
                      _hover={{
                        bg: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.4)',
                      }}
                    >
                      Learn More
                    </Button>
                    <Button
                      as="a"
                      href="https://play.texashodl.net/"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      bgGradient="linear(135deg, #3b82f6, #2563eb)"
                      color="white"
                      borderRadius="lg"
                      _hover={{
                        bgGradient: 'linear(135deg, #4f91ff, #3b82f6)',
                        transform: 'translateY(-1px)',
                      }}
                    >
                      Play Now
                    </Button>
                  </HStack>
                </VStack>
              </Box>

              {/* Placeholder for future partners */}
              <Box
                p={8}
                bg="rgba(255, 255, 255, 0.02)"
                backdropFilter="blur(10px)"
                borderRadius="2xl"
                border="1px dashed"
                borderColor="rgba(255, 255, 255, 0.15)"
                transition="all 0.3s ease"
                _hover={{
                  borderColor: 'rgba(100, 255, 218, 0.4)',
                }}
                minW={{ base: '280px', md: '320px' }}
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={3}>
                  <Text fontSize="4xl" color="gray.600">+</Text>
                  <Text fontWeight="600" color="gray.500" fontSize="md">
                    Your Club Here
                  </Text>
                  <Text fontSize="xs" color="gray.600" textAlign="center">
                    Become a license partner
                  </Text>
                </VStack>
              </Box>
            </Flex>

          </VStack>
        </Container>
      </Box>

      {/* Branding Video Section */}
      <Box py={20}>
        <Container maxW="5xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack spacing={6} align={{ base: 'center', lg: 'start' }} textAlign={{ base: 'center', lg: 'left' }}>
              <Text
                fontSize="sm"
                color="#64ffda"
                textTransform="uppercase"
                letterSpacing="0.15em"
                fontWeight="700"
              >
                White Label Solution
              </Text>
              <Heading
                size="xl"
                color="white"
                fontWeight="800"
                letterSpacing="-0.02em"
              >
                Your Brand, Your Club
              </Heading>
              <Text color="gray.400" fontSize="lg" lineHeight="1.8">
                Customize every aspect of your card club in real-time.
                Change colors, upload logos, and create a unique experience
                that matches your brand identity.
              </Text>
              <Button
                size="lg"
                variant="outline"
                color="#64ffda"
                borderColor="#64ffda"
                borderRadius="xl"
                _hover={{
                  bg: 'rgba(100, 255, 218, 0.1)',
                  transform: 'translateY(-2px)',
                }}
                transition="all 0.3s ease"
                onClick={() => setIsModalOpen(true)}
              >
                Get Early Access
              </Button>
            </VStack>

            <Box
              bg="rgba(255, 255, 255, 0.03)"
              backdropFilter="blur(10px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor="rgba(100, 255, 218, 0.2)"
              overflow="hidden"
              boxShadow="0 30px 60px rgba(0, 0, 0, 0.4)"
            >
              <video
                controls
                autoPlay
                muted
                loop
                playsInline
                style={{ width: '100%', height: 'auto', display: 'block' }}
              >
                <source
                  src="https://files.tommillerservices.com/ipfs/QmUK8s41y5wUwmGnU2Tz1qC2wP2wHdX7WAic2fj97Zv5by"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Container maxW="4xl" py={24}>
        <VStack
          spacing={8}
          p={{ base: 8, md: 16 }}
          bg="rgba(255, 255, 255, 0.03)"
          backdropFilter="blur(20px)"
          borderRadius="3xl"
          border="1px solid"
          borderColor="rgba(59, 130, 246, 0.3)"
          textAlign="center"
          position="relative"
          overflow="hidden"
        >
          {/* Background glow */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="150%"
            h="150%"
            bgGradient="radial(circle, rgba(59, 130, 246, 0.15) 0%, transparent 50%)"
            pointerEvents="none"
          />

          <VStack spacing={4} position="relative" zIndex="1">
            <Icon as={FaRocket} boxSize={12} color="#3b82f6" />
            <Heading
              as="h2"
              size="xl"
              color="white"
              fontWeight="900"
              letterSpacing="-0.02em"
            >
              Ready to Launch Your Card Club?
            </Heading>
            <Text
              fontSize="lg"
              color="gray.400"
              maxW="xl"
              lineHeight="1.8"
            >
              Join the future of decentralized card games. Be among the first to launch
              your own branded card club on the blockchain.
            </Text>

            <Button
              size="lg"
              px={12}
              py={8}
              mt={4}
              bgGradient="linear(135deg, #3b82f6, #64ffda)"
              color="white"
              fontSize="xl"
              fontWeight="bold"
              borderRadius="xl"
              boxShadow="0 10px 40px rgba(59, 130, 246, 0.4)"
              _hover={{
                bgGradient: 'linear(135deg, #4f91ff, #7affed)',
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 50px rgba(59, 130, 246, 0.5)',
              }}
              transition="all 0.3s ease"
              onClick={() => setIsModalOpen(true)}
            >
              Get Started Today
            </Button>

            <HStack spacing={6} pt={4} color="gray.500" fontSize="sm" flexWrap="wrap" justify="center">
              <HStack>
                <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
                <Text>No credit card</Text>
              </HStack>
              <HStack>
                <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
                <Text>60 second setup</Text>
              </HStack>
              <HStack>
                <Box w={1.5} h={1.5} borderRadius="full" bg="green.400" />
                <Text>Free to start</Text>
              </HStack>
            </HStack>
          </VStack>
        </VStack>
      </Container>

      <FooterWithFourColumns />

      {/* List Building Modal */}
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Box>
  );
};

export default Home;
